import {
  type dashboardChannelEnvironmentRepositorySchema,
  type dashboardRepositoryBranchSelectionSchema,
  dashboardListChannelEnvironmentRepositoriesResponseSchema,
  dashboardRefreshChannelEnvironmentRepositoryResponseSchema,
  dashboardSourceControlDiscoverBranchesResponseSchema,
  dashboardSourceControlProvidersListResponseSchema,
  dashboardSourceControlRepositoriesSummaryResponseSchema,
} from "@arti/schemas/dashboard";
import { isRecord } from "@arti/utilities";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { z } from "zod";

import type { DashboardRealtimeSubscribe } from "../../realtime/dashboardRealtimeTypes";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import { responseError } from "./useChannelEnvironment";

/**
 * Canonical attachment shape, inferred from the same schema the server validates
 * its response against. Same discipline as `useChannelEnvironment`: parse through
 * the shared schema, never hand-roll a parallel reader.
 */
export type ChannelWorkspaceRepository = z.infer<
  typeof dashboardChannelEnvironmentRepositorySchema
>;

export type WorkspaceRepositoryPermission =
  ChannelWorkspaceRepository["permission"];
export type ChannelWorkspaceRepositoryRefreshResponse = z.infer<
  typeof dashboardRefreshChannelEnvironmentRepositoryResponseSchema
>;

export class RefreshRepositoryRejectedUiError extends Error {
  constructor(
    message: string,
    readonly rejectReason: string | null,
  ) {
    super(message);
    this.name = "RefreshRepositoryRejectedUiError";
  }
}

export type AttachBranchSelection = z.input<
  typeof dashboardRepositoryBranchSelectionSchema
>;

export interface AttachRepositoryInput {
  /** Branch-create (PR-A): wins over `defaultBranch` when present. */
  branchSelection?: AttachBranchSelection;
  defaultBranch?: string | null;
  permission: WorkspaceRepositoryPermission;
  providerId: string;
  repositoryId: string;
}

/**
 * Typed attach refusal the modal can act on (collision choices, admin
 * repair hints). Anything else stays a plain Error via `responseError`.
 */
export class RepositoryAttachError extends Error {
  readonly code: string;
  readonly details: Record<string, unknown>;

  constructor(code: string, message: string, details: Record<string, unknown>) {
    super(message);
    this.name = "RepositoryAttachError";
    this.code = code;
    this.details = details;
  }
}

/** Codes the browser surfaces with structure rather than as flat text. */
const TYPED_ATTACH_ERRORS: ReadonlySet<string> = new Set([
  "allowlist_activation_would_unlock_policies",
  "allowlist_revoked",
  "branch_already_exists",
  "branch_create_requires_write",
  "branch_outcome_unknown",
  "branch_provision_failed",
  "branch_provision_in_progress",
  "push_broker_unavailable",
  "repository_push_in_progress",
]);

/**
 * R2.1 follower UX: the server waits ~5 s for a provision another request
 * owns, then answers 409 `branch_provision_in_progress`; the browser keeps
 * re-POSTing the IDENTICAL request (same idempotency key ⇒ zero extra
 * provider calls) until terminal or this budget runs out.
 */
const ATTACH_FOLLOW_BUDGET_MS = 90_000;
const ATTACH_FOLLOW_MIN_DELAY_MS = 1000;

async function readAttachRefusal(
  response: Response,
): Promise<RepositoryAttachError | Error> {
  let payload: unknown = null;
  try {
    payload = await response.clone().json();
  } catch {
    payload = null;
  }
  if (
    isRecord(payload) &&
    "error" in payload &&
    typeof payload.error === "string" &&
    TYPED_ATTACH_ERRORS.has(payload.error)
  ) {
    const { error: code, message, ...details } = payload;
    return new RepositoryAttachError(
      code,
      typeof message === "string" && message.length > 0
        ? message
        : `Attach refused (${code}).`,
      details,
    );
  }
  return await responseError(response);
}

interface WorkspaceRepositoriesState {
  error: string | null;
  loading: boolean;
  repositories: ChannelWorkspaceRepository[];
}

const POLL_MS = 8000;
const POST_RESTART_POLL_TIMEOUT_MS = 2 * 60 * 1000;

/**
 * Statuses that are still settling — while any attachment is in one of these the
 * hook polls so the panel reflects clone progress without an operator refresh.
 * Steady states (`ready`/`errored`) need no polling.
 */
const TRANSITIONAL_STATUSES: ReadonlySet<ChannelWorkspaceRepository["status"]> =
  new Set(["pending", "cloning", "detach_pending", "refreshing"]);

/**
 * A pending branch change keeps `status: "ready"` (the old checkout stays
 * usable), so transition detection must also watch `pendingBranch` — on a
 * running environment convergence lands within one reconcile pass and the
 * poll picks it up without an operator refresh.
 *
 * `pending` with the typed restart reason is the stable awaiting-restart state
 * (egress applies when the environment next starts) — nothing will change
 * until an operator restarts, so polling it would spin every viewer forever.
 * The restart action explicitly starts bounded post-restart polling.
 */
export function isSettling(repository: ChannelWorkspaceRepository): boolean {
  if (
    repository.status === "pending" &&
    repository.pendingReason === "egress_requires_restart"
  ) {
    // A pending branch on such a row applies at the same restart — equally
    // stable, so it must not re-enable polling either.
    return false;
  }
  return (
    TRANSITIONAL_STATUSES.has(repository.status) ||
    repository.pendingBranch != null
  );
}

export function shouldContinuePostRestartRepositoryPolling(
  repositories: readonly ChannelWorkspaceRepository[],
): boolean {
  return repositories.some(
    (repository) =>
      repository.pendingReason === "egress_requires_restart" ||
      isSettling(repository),
  );
}

export function useChannelWorkspaceRepositories(
  channelId: string | null,
  options: { enabled?: boolean; subscribe?: DashboardRealtimeSubscribe } = {},
): {
  acknowledgeAbsentDetach: (repoAttachmentId: string) => Promise<void>;
  attach: (
    input: AttachRepositoryInput,
    options?: { onProgress?: (message: string | null) => void },
  ) => Promise<void>;
  changeBranch: (repoAttachmentId: string, branch: string) => Promise<void>;
  detach: (repoAttachmentId: string) => Promise<void>;
  error: string | null;
  loading: boolean;
  refresh: () => Promise<ChannelWorkspaceRepository[] | null>;
  refreshAfterRestart: () => Promise<void>;
  refreshRepository: (
    repoAttachmentId: string,
    options?: { force?: boolean },
  ) => Promise<ChannelWorkspaceRepositoryRefreshResponse>;
  repositories: ChannelWorkspaceRepository[];
} {
  const enabled = options.enabled ?? true;
  const [state, setState] = useState<WorkspaceRepositoriesState>({
    error: null,
    loading: Boolean(channelId && enabled),
    repositories: [],
  });
  const activeRequestRef = useRef<{
    controller: AbortController;
    generation: number;
    promise: Promise<ChannelWorkspaceRepository[] | null>;
  } | null>(null);
  const deadlineTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(false);
  const postRestartPollDeadlineRef = useRef(0);
  const [postRestartPolling, setPostRestartPolling] = useState(false);
  const postRestartPollingRef = useRef(false);
  const refreshQueuedRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const realtime = useDashboardRealtime({
    enabled: options.subscribe === undefined && enabled && channelId !== null,
  });
  const subscribe = options.subscribe ?? realtime.subscribe;

  const refresh = useCallback(async (): Promise<
    ChannelWorkspaceRepository[] | null
  > => {
    if (!channelId || !enabled) {
      refreshQueuedRef.current = false;
      requestGenerationRef.current += 1;
      activeRequestRef.current?.controller.abort();
      activeRequestRef.current = null;
      setState({ error: null, loading: false, repositories: [] });
      return [];
    }
    const activeRequest = activeRequestRef.current;
    if (activeRequest) {
      refreshQueuedRef.current = true;
      return await activeRequest.promise;
    }
    refreshQueuedRef.current = false;
    const createRequest = () => {
      const generation = requestGenerationRef.current + 1;
      requestGenerationRef.current = generation;
      return { controller: new AbortController(), generation };
    };
    const executeRequest = async ({
      controller,
      generation,
    }: ReturnType<typeof createRequest>) => {
      if (mountedRef.current) {
        setState((previous) => ({ ...previous, loading: true }));
      }
      try {
        const response = await fetch(
          `/user/channels/${channelId}/environment/repositories`,
          { credentials: "include", signal: controller.signal },
        );
        if (response.status !== 404 && !response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const repositories =
          response.status === 404
            ? []
            : dashboardListChannelEnvironmentRepositoriesResponseSchema.parse(
                await response.json(),
              ).repositories;
        if (mountedRef.current && generation === requestGenerationRef.current) {
          setState({ error: null, loading: false, repositories });
        }
        return repositories;
      } catch (error) {
        if (
          controller.signal.aborted ||
          generation !== requestGenerationRef.current
        ) {
          return null;
        }
        if (mountedRef.current) {
          setState({
            error:
              error instanceof Error
                ? error.message
                : "Failed to load repositories",
            loading: false,
            repositories: [],
          });
        }
        return null;
      }
    };
    const firstRequest = createRequest();
    const activePromise = {
      current: null as Promise<ChannelWorkspaceRepository[] | null> | null,
    };
    const runRequest = async (
      request: ReturnType<typeof createRequest>,
    ): Promise<ChannelWorkspaceRepository[] | null> => {
      const repositories = await executeRequest(request);
      if (
        !refreshQueuedRef.current ||
        !mountedRef.current ||
        !channelId ||
        !enabled
      ) {
        return repositories;
      }
      refreshQueuedRef.current = false;
      const nextRequest = createRequest();
      if (!activePromise.current) {
        throw new Error("Repository refresh promise is unavailable.");
      }
      activeRequestRef.current = {
        ...nextRequest,
        promise: activePromise.current,
      };
      return await runRequest(nextRequest);
    };
    const promise = runRequest(firstRequest).finally(() => {
      if (activeRequestRef.current?.promise === promise) {
        activeRequestRef.current = null;
      }
    });
    activePromise.current = promise;
    activeRequestRef.current = { ...firstRequest, promise };
    return await promise;
  }, [channelId, enabled]);

  const hasTransitional = state.repositories.some(isSettling);
  const stopPostRestartPolling = useCallback((abortRequest: boolean) => {
    postRestartPollingRef.current = false;
    postRestartPollDeadlineRef.current = 0;
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
      deadlineTimerRef.current = null;
    }
    if (abortRequest) {
      refreshQueuedRef.current = false;
      requestGenerationRef.current += 1;
      activeRequestRef.current?.controller.abort();
      activeRequestRef.current = null;
      if (mountedRef.current) {
        setState((previous) => ({ ...previous, loading: false }));
      }
    }
    if (mountedRef.current) {
      setPostRestartPolling(false);
    }
  }, []);

  const refreshAfterRestart = useCallback(async () => {
    postRestartPollDeadlineRef.current =
      Date.now() + POST_RESTART_POLL_TIMEOUT_MS;
    postRestartPollingRef.current = true;
    setPostRestartPolling(true);
    if (deadlineTimerRef.current) {
      clearTimeout(deadlineTimerRef.current);
    }
    deadlineTimerRef.current = setTimeout(
      () => stopPostRestartPolling(true),
      POST_RESTART_POLL_TIMEOUT_MS,
    );
    const repositories = await refresh();
    if (
      repositories &&
      !shouldContinuePostRestartRepositoryPolling(repositories)
    ) {
      stopPostRestartPolling(false);
    }
  }, [refresh, stopPostRestartPolling]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      refreshQueuedRef.current = false;
      requestGenerationRef.current += 1;
      activeRequestRef.current?.controller.abort();
      activeRequestRef.current = null;
      if (deadlineTimerRef.current) {
        clearTimeout(deadlineTimerRef.current);
        deadlineTimerRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    void refresh();
    return () => {
      refreshQueuedRef.current = false;
      requestGenerationRef.current += 1;
      activeRequestRef.current?.controller.abort();
      activeRequestRef.current = null;
    };
  }, [refresh]);

  useEffect(() => {
    if (!channelId || !enabled || (!hasTransitional && !postRestartPolling)) {
      return undefined;
    }
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      timer = setTimeout(() => {
        void refresh().then((repositories) => {
          if (cancelled) {
            return;
          }
          if (
            postRestartPollingRef.current &&
            (Date.now() >= postRestartPollDeadlineRef.current ||
              (repositories !== null &&
                !shouldContinuePostRestartRepositoryPolling(repositories)))
          ) {
            stopPostRestartPolling(
              Date.now() >= postRestartPollDeadlineRef.current,
            );
            return;
          }
          if (
            repositories === null ||
            repositories.some(isSettling) ||
            postRestartPollingRef.current
          ) {
            schedule();
          }
        });
      }, POLL_MS);
    };
    schedule();
    return () => {
      cancelled = true;
      if (timer) {
        clearTimeout(timer);
      }
    };
  }, [
    channelId,
    enabled,
    hasTransitional,
    postRestartPolling,
    refresh,
    stopPostRestartPolling,
  ]);

  useEffect(() => {
    if (!channelId || !enabled) {
      return undefined;
    }
    const unsubscribe = subscribe(
      (message) => {
        if (
          message.type === "channel_environment_changed" &&
          message.payload.channel_id === channelId
        ) {
          void refresh();
        }
      },
      { topic: "internalEventTopic" },
    );
    return () => unsubscribe();
  }, [channelId, enabled, refresh, subscribe]);

  return useMemo(
    () => ({
      // Explicit operator resolution for a `checkout_absent` hold. The server
      // re-checks eligibility and the owner node re-proves absence; the query
      // parameter alone grants nothing.
      acknowledgeAbsentDetach: async (repoAttachmentId) => {
        if (!channelId) {
          return;
        }
        const response = await fetch(
          `/user/channels/${channelId}/environment/repositories/${repoAttachmentId}?resolution=acknowledge_absent_checkout`,
          { credentials: "include", method: "DELETE" },
        );
        if (!response.ok) {
          throw await responseError(response);
        }
        await refresh();
      },
      attach: async (input, attachOptions = {}) => {
        if (!channelId) {
          return;
        }
        const body = JSON.stringify(input);
        const startedAt = Date.now();
        for (;;) {
          const response = await fetch(
            `/user/channels/${channelId}/environment/repositories`,
            {
              body,
              credentials: "include",
              headers: { "Content-Type": "application/json" },
              method: "POST",
            },
          );
          if (response.ok) {
            attachOptions.onProgress?.(null);
            break;
          }
          const refusal = await readAttachRefusal(response);
          if (
            refusal instanceof RepositoryAttachError &&
            refusal.code === "branch_provision_in_progress" &&
            Date.now() - startedAt < ATTACH_FOLLOW_BUDGET_MS
          ) {
            attachOptions.onProgress?.(
              "Another request is creating this branch — waiting for it…",
            );
            const retryAfter = refusal.details.retryAfterMs;
            await new Promise((resolve) => {
              setTimeout(
                resolve,
                Math.max(
                  ATTACH_FOLLOW_MIN_DELAY_MS,
                  typeof retryAfter === "number" ? retryAfter : 0,
                ),
              );
            });
            continue;
          }
          attachOptions.onProgress?.(null);
          throw refusal;
        }
        await refresh();
      },
      changeBranch: async (repoAttachmentId, branch) => {
        if (!channelId) {
          return;
        }
        const response = await fetch(
          `/user/channels/${channelId}/environment/repositories/${repoAttachmentId}`,
          {
            body: JSON.stringify({ branch }),
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            method: "PATCH",
          },
        );
        if (!response.ok) {
          throw await responseError(response);
        }
        await refresh();
      },
      detach: async (repoAttachmentId) => {
        if (!channelId) {
          return;
        }
        const response = await fetch(
          `/user/channels/${channelId}/environment/repositories/${repoAttachmentId}`,
          { credentials: "include", method: "DELETE" },
        );
        if (!response.ok) {
          throw await responseError(response);
        }
        await refresh();
      },
      error: state.error,
      loading: state.loading,
      refresh,
      refreshAfterRestart,
      refreshRepository: async (repoAttachmentId, refreshOptions = {}) => {
        if (!channelId) {
          throw new Error("No channel selected");
        }
        const response = await fetch(
          `/user/channels/${channelId}/environment/repositories/${repoAttachmentId}/refresh`,
          {
            body: JSON.stringify({ force: refreshOptions.force === true }),
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            method: "POST",
          },
        );
        if (!response.ok) {
          const rejectedError = await readRefreshRejectedError(response);
          if (rejectedError) {
            throw rejectedError;
          }
          throw await responseError(response);
        }
        const parsed =
          dashboardRefreshChannelEnvironmentRepositoryResponseSchema.parse(
            await response.json(),
          );
        await refresh();
        return parsed;
      },
      repositories: state.repositories,
    }),
    [channelId, refresh, refreshAfterRestart, state],
  );
}

const refreshRejectedPayloadSchema = z.object({
  error: z.literal("REFRESH_REJECTED"),
  message: z.string(),
  reject_reason: z.string().nullable().optional(),
});

async function readRefreshRejectedError(
  response: Response,
): Promise<RefreshRepositoryRejectedUiError | null> {
  if (response.status !== 409) {
    return null;
  }
  const payload = await response
    .clone()
    .json()
    .catch(() => null);
  const parsed = refreshRejectedPayloadSchema.safeParse(payload);
  if (!parsed.success) {
    return null;
  }
  return new RefreshRepositoryRejectedUiError(
    parsed.data.message,
    parsed.data.reject_reason ?? null,
  );
}

export const __testing = { readRefreshRejectedError };

/* ============================================================ */
/* Source-control catalog (attach picker)                        */
/* ============================================================ */

export interface SourceControlProviderOption {
  displayName: string;
  isEnabled: boolean;
  providerId: string;
  type: string;
}

export interface SourceControlRepositoryOption {
  defaultBranch: string | null;
  remoteSlug: string;
  repositoryId: string;
}

export interface SourceControlBranchOption {
  isDefault: boolean;
  name: string;
}

/**
 * Standalone branch discovery (shared by the attach picker and the
 * change-branch dialog) — plain async so dialogs can call it on open without
 * mounting the provider catalog hook.
 */
export async function discoverRepositoryBranches(
  providerId: string,
  repositorySlug: string,
): Promise<SourceControlBranchOption[]> {
  const params = new URLSearchParams({ repositorySlug });
  const response = await fetch(
    `/user/source-control/providers/${encodeURIComponent(providerId)}/discover-branches?${params.toString()}`,
    { credentials: "include" },
  );
  if (!response.ok) {
    throw await responseError(response);
  }
  const parsed = dashboardSourceControlDiscoverBranchesResponseSchema.parse(
    await response.json(),
  );
  return parsed.branches.map((branch) => ({
    isDefault: branch.is_default,
    name: branch.name,
  }));
}

interface CatalogState {
  error: string | null;
  loading: boolean;
  providers: SourceControlProviderOption[];
}

export async function loadWorkspaceRepositoryOptions(providerId: string) {
  const response = await fetch("/user/source-control/repositories/summary", {
    credentials: "include",
  });
  if (!response.ok) {
    throw await responseError(response);
  }
  const parsed = dashboardSourceControlRepositoriesSummaryResponseSchema.parse(
    await response.json(),
  );
  return parsed.repositories
    .filter((repository) => repository.provider_id === providerId)
    .map((repository) => ({
      defaultBranch: repository.default_branch,
      remoteSlug: repository.remote_slug,
      repositoryId: repository.repository_id,
    }));
}

/**
 * Lazily loads the org's source-control providers (and, on demand, a provider's
 * repositories) to drive the attach-repo picker. Only fetches when `enabled` —
 * the modal mounts it so the catalog isn't pulled until an operator opens it.
 */
export function useSourceControlCatalog(enabled: boolean): {
  error: string | null;
  loadBranches: (
    providerId: string,
    repositorySlug: string,
  ) => Promise<SourceControlBranchOption[]>;
  loadRepositories: (
    providerId: string,
  ) => Promise<SourceControlRepositoryOption[]>;
  loading: boolean;
  providers: SourceControlProviderOption[];
} {
  const [state, setState] = useState<CatalogState>({
    error: null,
    loading: enabled,
    providers: [],
  });

  useEffect(() => {
    if (!enabled) {
      return;
    }
    let cancelled = false;
    void (async () => {
      setState((previous) => ({ ...previous, error: null, loading: true }));
      try {
        const response = await fetch("/user/source-control/providers", {
          credentials: "include",
        });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }
        const parsed = dashboardSourceControlProvidersListResponseSchema.parse(
          await response.json(),
        );
        if (cancelled) {
          return;
        }
        setState({
          error: null,
          loading: false,
          providers: parsed.providers
            .filter((provider) => provider.is_enabled)
            .map((provider) => ({
              displayName: provider.display_name,
              isEnabled: provider.is_enabled,
              providerId: provider.provider_id,
              type: provider.type,
            })),
        });
      } catch (error) {
        if (cancelled) {
          return;
        }
        setState({
          error:
            error instanceof Error ? error.message : "Failed to load providers",
          loading: false,
          providers: [],
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [enabled]);

  const loadRepositories = useCallback(loadWorkspaceRepositoryOptions, []);

  const loadBranches = useCallback(discoverRepositoryBranches, []);

  return {
    error: state.error,
    loadBranches,
    loadRepositories,
    loading: state.loading,
    providers: state.providers,
  };
}
